import { ChevronDown, ChevronUp } from 'lucide-react'
import { useId, useState } from 'react'
import { api } from '../../lib/api'
import { budgetClasses, formatHours, hasBudgetData } from '../../lib/budget-view'
import { dayKey } from '../../lib/calendar'
import { useBudgetProjects } from '../../lib/use-budget-projects'
import { useMediaQuery } from '../../lib/use-media-query'
import { cn } from '../../lib/utils'
import { BudgetProjectSelect } from '../budget/budget-project-select'
import { BudgetHint, BudgetPeriodLabel, BudgetRates } from '../budget/budget-summary'

function stored(key: string) {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

export function BudgetStrip({ cursorISO }: { cursorISO: string }) {
  const [open, setOpen] = useState(() => stored('calendar-budget-strip-open') !== 'false')
  const [preferredId, setPreferredId] = useState(() => stored('calendar-budget-project'))
  const phone = useMediaQuery('(max-width: 639px)')
  const id = useId()
  const budget = useBudgetProjects(cursorISO, preferredId, (projectId, date) =>
    api.getBudgetStatus(projectId, { date }),
  )
  if (!budget.hasActivePeriod) return null
  const { status } = budget
  const pct = status?.contingentHours
    ? ((status.usedHours ?? 0) / status.contingentHours) * 100
    : null
  const colors = budgetClasses(pct)
  const isToday = cursorISO === dayKey(new Date())
  return (
    <section
      aria-label="Abrechenbar im Kalender"
      className="shrink-0 rounded-xl border border-border-subtle bg-bg-elevated px-3 py-2 space-y-2"
    >
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1 sm:max-w-[240px]">
          <BudgetProjectSelect
            {...budget}
            onChange={(projectId) => {
              setPreferredId(projectId)
              try {
                localStorage.setItem('calendar-budget-project', projectId)
              } catch {
                /* storage unavailable */
              }
            }}
          />
        </div>
        {!phone && hasBudgetData(status) && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            <span>
              {isToday ? 'Heute' : 'Tag'} Ø <strong>{formatHours(status.perDay)}</strong>
            </span>
            <span>
              Woche <strong>{formatHours(status.weekHours)}</strong>
            </span>
            <span>
              Monat <strong>{formatHours(status.monthHours)}</strong>
            </span>
            <span>gebucht {formatHours(status.ownTodayHours)}</span>
          </div>
        )}
        <span className={cn('ml-auto shrink-0 text-xs', colors.text)}>
          Rest {formatHours(status?.remainingHours ?? null)}
        </span>
        {phone && (
          <button
            type="button"
            aria-label={open ? 'Kontingent einklappen' : 'Kontingent ausklappen'}
            aria-expanded={open}
            aria-controls={open ? id : undefined}
            className="p-2.5 -mr-2 rounded-lg hover:bg-bg-overlay touch:opacity-100"
            onClick={() => {
              setOpen(!open)
              try {
                localStorage.setItem('calendar-budget-strip-open', String(!open))
              } catch {
                /* storage unavailable */
              }
            }}
          >
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        )}
      </div>
      {(!phone || open) && (
        <div id={id} className="space-y-2">
          {hasBudgetData(status) ? (
            <>
              {phone && <BudgetRates status={status} dayLabel={isToday ? 'Heute Ø' : 'Tag Ø'} />}
              <div className="flex flex-wrap items-center justify-between gap-1 text-[10px] text-text-tertiary">
                <span>
                  Deine Raten · <BudgetPeriodLabel status={status} />
                </span>
                <span>
                  Rest {formatHours(status.remainingHours)} / {formatHours(status.contingentHours)}
                </span>
              </div>
              <div className="h-1 overflow-hidden rounded-full bg-bg-overlay">
                <div
                  className={cn('h-full', colors.bar)}
                  style={{ width: `${Math.min(100, Math.max(0, pct ?? 0))}%` }}
                />
              </div>
              {phone && <BudgetHint />}
            </>
          ) : (
            <p className="text-xs text-text-tertiary">
              {budget.isLoading
                ? 'Lade Kontingent …'
                : status
                  ? 'Kein Kontingent für diesen Tag'
                  : 'Kontingent nicht verfügbar.'}
            </p>
          )}
        </div>
      )}
    </section>
  )
}
